import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";
import { useQueryClient } from "react-query";

import { CloseCircleOutlined, TableOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, Modal, Select, Tabs } from "antd";
import { EVA_RESEARCH_NAME } from "constants/app.constants";
import { DEPTH_MISSING_WARNING } from "constants/scenario.constants";
import styled from "styled-components";

import usePlayTo3DDataPrivateSources from "hooks/usePlayTo3DDataPrivateSources";

import { getGroupedPlayZonesByPeriod } from "api/map";

import useGeoMapLayers from "components/geo-map/hooks/getGeoMapLayers";
import { IconSpinner } from "components/icons";
import { ScenarioField } from "components/stick-data-management/types/field";

import { useProjectContext } from "../project/projects/context";
import StickDataManagement from "../stick-data-management/StickDataManagement";
import { ScenarioToastContainerId } from "./PadScenario";
import { usePadScenario } from "./contexts/usePadScenario";
import { StickRow } from "./hooks";
import useApplyStickDepths from "./hooks/useApplyStickDepths";
import { GetScenarioListKey } from "./hooks/useScenarioList";
import { ScenarioStickListKey } from "./hooks/useScenarioSticks";
import useUploadCsvSurveys from "./hooks/useUploadCsvSurveys";
import useUploadLegacyCsvSurveys from "./hooks/useUploadLegacyCsvSurveys";
import useUploadShapefileSurveys from "./hooks/useUploadShapefileSurveys";
import { Scenario, Stick } from "./models/scenario";
import {
  KnownSchemaColumn,
  SURVEY_REQUIRED_COLUMNS,
  UTM_ZONE_PATTERN,
  autoMapCsvHeaders,
  extractCsvHeaders,
  extractLegacyCsvHeaders
} from "./utils/csvColumnMatcher";
import {
  handleLegacyCsvUpload,
  handleTwoFileCsvUpload,
  handleZipUpload
} from "./utils/fileUploadController";
import { VERTICAL_OFFSET_PATTERN, sticksHaveDepth } from "./utils/scenarioHelpers";

/**
 * Optimistically patches `allSticksHaveDepth` on the matching scenario in all
 * cached scenario-list queries so the ScenarioList warning icon updates immediately.
 */
function patchScenarioListDepthFlag(
  queryClient: import("react-query").QueryClient,
  projectId: string | undefined,
  scenarioId: string | undefined,
  allSticksHaveDepth: boolean
): void {
  if (!scenarioId || !projectId) return;
  queryClient.setQueryData<Scenario[] | undefined>(
    [GetScenarioListKey, projectId],
    (old) =>
      old?.map((s) => (s._id.$oid === scenarioId ? { ...s, allSticksHaveDepth } : s))
  );
}

export interface ModalControls {
  open: boolean;
  show: () => void;
  hide: () => void;
}

export const useModal = (): ModalControls => {
  const [open, setOpen] = useState(false);

  const show = useCallback(() => setOpen(true), []);
  const hide = useCallback(() => setOpen(false), []);

  return useMemo(
    () => ({
      open,
      show,
      hide
    }),
    [open, show, hide]
  );
};

interface HeaderButtonsProps {
  scenarioId?: string;
  scenarioName?: string;
  sticks?: Stick[];
  stickData?: StickRow[];
}

// ─── Styled components ────────────────────────────────────────────────────────

const RootContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin: 5px;
  padding-right: 5px;
`;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
    max-height: 80vh;
    overflow-y: auto;
  }
`;

const IdPropertyDescription = styled.p`
  margin-bottom: 16px;
  color: rgba(0, 0, 0, 0.65);
`;

// ─── Tabbed upload modal ──────────────────────────────────────────────────────

const UploadTabsWrapper = styled.div`
  /* Pull the Ant Design tab bar flush with the modal header border */
  margin: -1px -24px 0;

  .ant-tabs-nav {
    padding: 0 24px;
    margin-bottom: 0;
  }

  .ant-tabs-content-holder {
    padding: 0;
  }
`;

const ShapefileTabBody = styled.div`
  padding: 20px 24px 24px;
`;

const TypeCardGuidanceHighlight = styled.span`
  font-family: monospace;
  font-size: 12px;
  background: #f3f4f6;
  padding: 1px 4px;
  border-radius: 3px;
  color: #374151;
`;

// ─── Two-file CSV tab ─────────────────────────────────────────────────────────

const CsvTabBody = styled.div`
  padding: 20px 24px 24px;
  max-height: calc(80vh - 46px);
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 4px;
  }
  ::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }

  /* Teal border for required-field mapped selects */
  .csv-select-mapped .ant-select-selector {
    border-color: #2dd4bf !important;
  }
  .csv-select-mapped.ant-select-focused .ant-select-selector {
    border-color: #2dd4bf !important;
    box-shadow: 0 0 0 2px rgba(45, 212, 191, 0.2) !important;
  }
`;

const CombinedDropZone = styled.div<{ $active: boolean; $allFilled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 80px;
  border: 2px dashed
    ${({ $active, $allFilled }) =>
      $active ? "#2dd4bf" : $allFilled ? "#6ee7b7" : "#d1d5db"};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? "rgba(45,212,191,0.05)" : "#fafafa")};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  padding: 16px 20px;
  user-select: none;

  &:hover {
    border-color: #2dd4bf;
    background: rgba(45, 212, 191, 0.04);
  }
`;

const CombinedDropZoneText = styled.span`
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  pointer-events: none;
`;

const CombinedDropZoneFiles = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  pointer-events: none;
`;

const CombinedDropZoneFileChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: monospace;
  font-size: 12.5px;
  color: #1f2937;
  background: #f3f4f6;
  padding: 2px 8px 2px 10px;
  border-radius: 99px;
  border: 1px solid #e5e7eb;
  pointer-events: none;
`;

const CombinedDropZoneFileChipLabel = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 2px;
  pointer-events: none;
`;

const CsvColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 20px;
`;

const FileSection = styled.div`
  min-width: 0;
`;

const FileSectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;

  &:hover {
    color: #ef4444;
  }
`;

const MappingSection = styled.div`
  margin-top: 10px;
`;

const MappingHint = styled.div`
  font-size: 11.5px;
  color: #9ca3af;
  margin-bottom: 6px;
`;

const MappingTableContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  overflow: hidden;
`;

const MappingTableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.9fr;
  gap: 10px;
  padding: 7px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const MappingTableHeaderCell = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MappingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.9fr;
  gap: 10px;
  align-items: center;
  padding: 6px 12px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
`;

const RequiredFieldLabel = styled.span`
  font-family: monospace;
  font-size: 13px;
  color: #1f2937;
`;

const RequiredBadge = styled.span`
  font-size: 10px;
  color: #ef4444;
  margin-left: 3px;
`;

const ModalContentWrapper = styled.div`
  position: relative;
`;

const ModalLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 10;
  border-radius: 4px;
  pointer-events: none;
`;

const ModalLoadingTip = styled.span`
  font-size: 13px;
  color: #374151;
`;

const InlineError = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  color: #b91c1c;
  line-height: 1.5;
`;

const LegacyFormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
`;

const FormField = styled.div``;

const FormFieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DepthHint = styled.div`
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 6px;
  margin-bottom: 8px;
`;

const LegacyMappingWrapper = styled.div`
  margin-top: 20px;
`;

// ─── CSV modal state ──────────────────────────────────────────────────────────

/** One row in the mapping table: a required backend field → a CSV column chosen by the user */
interface FieldMappingRow {
  /** The fixed backend field name (e.g. "UWI", "Surface X") */
  requiredField: string;
  /** The CSV column the user has picked, or "" if not yet mapped */
  csvColumn: string;
}

interface FileSlot {
  file: File | null;
  headers: string[];
  mappingRows: FieldMappingRow[];
}

const makeEmptySlot = (requiredFields: readonly string[]): FileSlot => ({
  file: null,
  headers: [],
  mappingRows: requiredFields.map((f) => ({ requiredField: f, csvColumn: "" }))
});

interface TwoFileCsvModalState {
  open: boolean;
  surface: FileSlot;
  stations: FileSlot;
  combinedDragActive: boolean;
}

/** Required column names for the surface locations file (exact match to spec) */
const SURFACE_REQUIRED_FIELDS = ["UWI", "Surface X", "Surface Y", "KB"] as const;

/** Required column names for the survey stations file (exact match to spec) */
const STATIONS_REQUIRED_FIELDS = [
  "UWI",
  "X",
  "Y",
  "MD",
  "TVD",
  "Azimuth",
  "Inclination"
] as const;

const CSV_MODAL_INITIAL_STATE: TwoFileCsvModalState = {
  open: false,
  surface: makeEmptySlot(SURFACE_REQUIRED_FIELDS),
  stations: makeEmptySlot(STATIONS_REQUIRED_FIELDS),
  combinedDragActive: false
};

type FileTarget = "surface" | "stations";

// ─── Shapefile tab view ───────────────────────────────────────────────────────

/**
 * Discriminated union for the Shapefile tab. Either the drop zone is shown,
 * or the inline ID-property selector is shown when the uploaded ZIP exposes
 * multiple candidate properties.
 */
type ShapefileView =
  | { kind: "dropzone" }
  | {
      kind: "selectIdProperty";
      file: File;
      properties: string[];
      selectedProperty: string;
    };

type TwoFileCsvModalAction =
  | { type: "OPEN" }
  | { type: "RESET" }
  | {
      type: "SET_FILE";
      target: FileTarget;
      payload: { file: File; headers: string[]; mappingRows: FieldMappingRow[] };
    }
  | { type: "CLEAR_FILE"; target: FileTarget }
  | {
      type: "SET_MAPPING_ROW";
      target: FileTarget;
      payload: { index: number; csvColumn: string };
    }
  | { type: "SET_COMBINED_DRAG_ACTIVE"; payload: boolean };

const twoFileCsvModalReducer = (
  state: TwoFileCsvModalState,
  action: TwoFileCsvModalAction
): TwoFileCsvModalState => {
  switch (action.type) {
    case "OPEN":
      return { ...CSV_MODAL_INITIAL_STATE, open: true };

    case "RESET":
      return CSV_MODAL_INITIAL_STATE;

    case "SET_FILE":
      return {
        ...state,
        [action.target]: {
          ...state[action.target],
          file: action.payload.file,
          headers: action.payload.headers,
          mappingRows: action.payload.mappingRows
        }
      };

    case "CLEAR_FILE": {
      const requiredFields =
        action.target === "surface" ? SURFACE_REQUIRED_FIELDS : STATIONS_REQUIRED_FIELDS;
      return {
        ...state,
        [action.target]: makeEmptySlot(requiredFields)
      };
    }

    case "SET_MAPPING_ROW":
      return {
        ...state,
        [action.target]: {
          ...state[action.target],
          mappingRows: state[action.target].mappingRows.map((row, i) =>
            i === action.payload.index
              ? { ...row, csvColumn: action.payload.csvColumn }
              : row
          )
        }
      };

    case "SET_COMBINED_DRAG_ACTIVE":
      return { ...state, combinedDragActive: action.payload };

    default:
      return state;
  }
};

// ─── File classification ──────────────────────────────────────────────────────

/**
 * Inspects the parsed CSV headers of a file and returns which slot it belongs
 * to based on characteristic column names:
 *   - Surface locations file contains "KB"
 *   - Survey stations file contains "Azimuth" or "MD"
 *
 * Comparison is case-insensitive. Returns `null` when neither signal is found.
 *
 * Precedence: "KB" is checked first. Per the two-file spec, "KB" appears
 * exclusively in the surface locations file header row, so it is the
 * strongest signal. "Azimuth" and "MD" are checked second; they are the
 * characteristic columns of the survey stations file.
 *
 * Edge case: a vendor export that includes a "KB" column at every survey
 * station would be misclassified as a surface file. This is considered an
 * out-of-spec file and is not supported.
 */
export const classifyCsvFile = (headers: string[]): FileTarget | null => {
  const normalised = headers.map((h) => h.toLowerCase().trim());

  // "KB" is the strongest surface-file signal and is checked first.
  if (normalised.some((h) => h === "kb")) return "surface";
  // "Azimuth" and "MD" are the characteristic stations-file columns.
  if (normalised.some((h) => h === "azimuth" || h === "md")) return "stations";

  return null;
};

// ─── Auto-map helpers ─────────────────────────────────────────────────────────

/**
 * Pre-selects a CSV column for each required field using case-insensitive
 * exact matching only (no fuzzy / Levenshtein matching).
 *
 * This is intentionally simpler than `autoMapCsvHeaders` in csvColumnMatcher.ts,
 * which uses alias lists and Levenshtein distance for legacy single-file uploads.
 * The two-file spec defines precise, fixed column names (e.g. "Surface X",
 * "Azimuth"), so an exact match is both sufficient and safer — fuzzy matching
 * would risk silently mapping the wrong column when vendor exports use similar
 * but distinct names.
 */
const autoMapRequiredFields = (
  requiredFields: readonly string[],
  csvHeaders: readonly string[]
): FieldMappingRow[] => {
  const claimed = new Set<string>();

  return requiredFields.map((field) => {
    const normalised = field.toLowerCase().replace(/\s+/g, " ").trim();
    const match = csvHeaders.find(
      (h) => h.toLowerCase().replace(/\s+/g, " ").trim() === normalised && !claimed.has(h)
    );

    if (match) {
      claimed.add(match);
      return { requiredField: field, csvColumn: match };
    }

    return { requiredField: field, csvColumn: "" };
  });
};

// ─── Form validation ──────────────────────────────────────────────────────────

/**
 * The two-file CSV form is valid when both files are attached and every
 * required field has a CSV column selected.
 */
const isTwoFileCsvFormValid = (state: TwoFileCsvModalState): boolean => {
  if (!state.surface.file || !state.stations.file) return false;

  const allSurfaceMapped = state.surface.mappingRows.every((r) => r.csvColumn !== "");
  const allStationsMapped = state.stations.mappingRows.every((r) => r.csvColumn !== "");

  return allSurfaceMapped && allStationsMapped;
};

// ─── Legacy single-file CSV state ─────────────────────────────────────────────

type SupportedDatum = "NAD83" | "NAD27" | "WGS84";

interface LegacyMappingRow {
  schemaColumn: KnownSchemaColumn;
  label: string;
  csvHeader: string;
}

interface LegacyCsvState {
  file: File | null;
  headers: string[];
  utmZone: string;
  datum: SupportedDatum | "";
  mappingRows: LegacyMappingRow[];
  dragActive: boolean;
}

const LEGACY_INITIAL_MAPPING_ROWS: LegacyMappingRow[] = SURVEY_REQUIRED_COLUMNS.map(
  (col) => ({
    schemaColumn: col.value,
    label: col.label,
    csvHeader: ""
  })
);

const LEGACY_CSV_INITIAL_STATE: LegacyCsvState = {
  file: null,
  headers: [],
  utmZone: "",
  datum: "",
  mappingRows: LEGACY_INITIAL_MAPPING_ROWS,
  dragActive: false
};

type LegacyCsvAction =
  | { type: "RESET" }
  | {
      type: "SET_FILE";
      payload: { file: File; headers: string[]; mappingRows: LegacyMappingRow[] };
    }
  | { type: "CLEAR_FILE" }
  | { type: "SET_UTM_ZONE"; payload: string }
  | { type: "SET_DATUM"; payload: SupportedDatum | "" }
  | { type: "SET_MAPPING_ROW"; payload: { index: number; csvHeader: string } }
  | { type: "SET_DRAG_ACTIVE"; payload: boolean };

const legacyCsvReducer = (
  state: LegacyCsvState,
  action: LegacyCsvAction
): LegacyCsvState => {
  switch (action.type) {
    case "RESET":
      return LEGACY_CSV_INITIAL_STATE;

    case "SET_FILE":
      return {
        ...state,
        file: action.payload.file,
        headers: action.payload.headers,
        mappingRows: action.payload.mappingRows
      };

    case "CLEAR_FILE":
      return {
        ...state,
        file: null,
        headers: [],
        mappingRows: LEGACY_INITIAL_MAPPING_ROWS
      };

    case "SET_UTM_ZONE":
      return { ...state, utmZone: action.payload };

    case "SET_DATUM":
      return { ...state, datum: action.payload };

    case "SET_MAPPING_ROW":
      return {
        ...state,
        mappingRows: state.mappingRows.map((row, i) =>
          i === action.payload.index
            ? { ...row, csvHeader: action.payload.csvHeader }
            : row
        )
      };

    case "SET_DRAG_ACTIVE":
      return { ...state, dragActive: action.payload };

    default:
      return state;
  }
};

/** Canonical columns that are always required regardless of depth path. */
const ALWAYS_REQUIRED_COLUMNS: KnownSchemaColumn[] = [
  "uwi",
  "md",
  "inclination",
  "azimuth",
  "x",
  "y"
];

/**
 * The legacy single-file CSV form is valid when:
 *  - A file is attached
 *  - UTM zone matches the expected pattern (1–60 followed by N or S)
 *  - A datum is selected
 *  - All always-required columns are mapped
 *  - At least one depth path is satisfied: subsea OR (tvd AND kb)
 */
const isLegacyCsvFormValid = (state: LegacyCsvState): boolean => {
  if (!state.file) return false;
  if (!state.utmZone || !UTM_ZONE_PATTERN.test(state.utmZone)) return false;
  if (!state.datum) return false;

  const mappedSet = new Set(
    state.mappingRows.filter((r) => r.csvHeader).map((r) => r.schemaColumn)
  );

  if (!ALWAYS_REQUIRED_COLUMNS.every((col) => mappedSet.has(col))) return false;

  const hasSubsea = mappedSet.has("subsea");
  const hasDepthPair = mappedSet.has("tvd") && mappedSet.has("kb");
  return hasSubsea || hasDepthPair;
};

// ─── Component ────────────────────────────────────────────────────────────────

const HeaderButtons: FC<HeaderButtonsProps> = ({
  scenarioId,
  scenarioName,
  stickData,
  sticks = []
}) => {
  const { mutateAsync: uploadShapefile, isLoading: isShapefileLoading } =
    useUploadShapefileSurveys();
  const { mutateAsync: uploadCsv, isLoading: isCsvLoading } = useUploadCsvSurveys();
  const { mutateAsync: uploadLegacyCsv, isLoading: isLegacyCsvLoading } =
    useUploadLegacyCsvSurveys();
  const { mutateAsync: applyDepths, isLoading: isApplyingDepths } = useApplyStickDepths();
  const queryClient = useQueryClient();
  const { state, dispatch } = usePadScenario();
  const { selectedProjectId } = useProjectContext();
  const { data: geoMapLayers } = useGeoMapLayers();
  const { data: privateSources } = usePlayTo3DDataPrivateSources();

  // ─── Depth config state (shown after a no-depth upload) ──────────────────
  const [depthConfigOpen, setDepthConfigOpen] = useState(false);
  const [depthModelSource, setDepthModelSource] = useState("");
  const [depthZone, setDepthZone] = useState("");
  const [depthOffset, setDepthOffset] = useState("");
  const [depthError, setDepthError] = useState<string | null>(null);
  const [ipdbZones, setIpdbZones] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    getGroupedPlayZonesByPeriod()
      .then((res) => {
        if (!active) return;
        const zones: string[] = res.data.flatMap((p) =>
          p.plays.flatMap((pl) => pl.zones as string[])
        );
        setIpdbZones([...new Set(zones)].sort());
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // ─── Shapefile tab view state ─────────────────────────────────────────────
  const [shapefileView, setShapefileView] = useState<ShapefileView>({ kind: "dropzone" });
  const [shapefileError, setShapefileError] = useState<string | null>(null);

  // ─── Upload error state (inline, shown inside the modal) ─────────────────
  const [csvError, setCsvError] = useState<string | null>(null);

  // ─── Unified upload modal ─────────────────────────────────────────────────
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [activeUploadTab, setActiveUploadTab] = useState<"shapefile" | "csv">("csv");
  const [zipDragActive, setZipDragActive] = useState(false);

  // ─── Two-file CSV state (useReducer) ─────────────────────────────────────
  const [csvModal, dispatchCsvModal] = useReducer(
    twoFileCsvModalReducer,
    CSV_MODAL_INITIAL_STATE
  );

  // ─── Legacy single-file CSV state ──────────────────────────────────────
  const [legacyCsv, dispatchLegacyCsv] = useReducer(
    legacyCsvReducer,
    LEGACY_CSV_INITIAL_STATE
  );

  // Derive the active CSV format from whichever state has a file loaded.
  // null = neutral (nothing loaded yet).
  const detectedCsvFormat: "twoFile" | "legacy" | null = legacyCsv.file
    ? "legacy"
    : csvModal.surface.file || csvModal.stations.file
    ? "twoFile"
    : null;

  const isLoading = isShapefileLoading || isCsvLoading || isLegacyCsvLoading;
  const modal = useModal();

  const handleFieldsChange = useCallback(
    (updatedFields: ScenarioField[]) => {
      dispatch({
        type: "SET_SCENARIO_FIELDS",
        payload: updatedFields
      });
    },
    [dispatch]
  );

  // ─── Unified upload modal handlers ───────────────────────────────────────

  const handleUploadSurveysClick = useCallback(() => {
    if (!scenarioId || isLoading) return;
    setUploadModalOpen(true);
  }, [scenarioId, isLoading]);

  const handleUploadModalCancel = useCallback(() => {
    setUploadModalOpen(false);
    setDepthConfigOpen(false);
    setDepthModelSource("");
    setDepthZone("");
    setDepthOffset("");
    setDepthError(null);
    dispatchCsvModal({ type: "RESET" });
    dispatchLegacyCsv({ type: "RESET" });
    setShapefileView({ kind: "dropzone" });
    setShapefileError(null);
    setCsvError(null);
  }, []);

  // Hidden file inputs for ZIP and CSV slot uploads
  const [zipInputRef, setZipInputRef] = useState<HTMLInputElement | null>(null);
  const [csvInputRef, setCsvInputRef] = useState<HTMLInputElement | null>(null);

  const handleZipDropZoneClick = useCallback(() => {
    if (!zipInputRef) return;
    zipInputRef.value = "";
    zipInputRef.click();
  }, [zipInputRef]);

  // ─── Shapefile handlers ───────────────────────────────────────────────────

  const handleIdPropertySelect = useCallback((value: string) => {
    setShapefileView((prev) =>
      prev.kind === "selectIdProperty" ? { ...prev, selectedProperty: value } : prev
    );
  }, []);

  const handleIdPropertyConfirm = useCallback(async () => {
    if (shapefileView.kind !== "selectIdProperty") return;
    const { file, selectedProperty } = shapefileView;
    if (!selectedProperty) return;

    setShapefileError(null);

    const priorIds = new Set(
      (
        queryClient.getQueryData<Stick[]>([ScenarioStickListKey, { $oid: scenarioId }]) ??
        []
      ).map((s) => s.id)
    );

    const success = await handleZipUpload({
      file,
      scenarioId,
      upload: uploadShapefile,
      queryClient,
      toastContainerId: ScenarioToastContainerId,
      onError: setShapefileError,
      idProperty: selectedProperty
    });

    if (success) {
      const freshSticks =
        queryClient.getQueryData<Stick[]>([ScenarioStickListKey, { $oid: scenarioId }]) ??
        [];
      const scenarioHasDepth = freshSticks.length === 0 || sticksHaveDepth(freshSticks);
      patchScenarioListDepthFlag(
        queryClient,
        selectedProjectId,
        scenarioId,
        scenarioHasDepth
      );
      const newSticks = freshSticks.filter((s) => !priorIds.has(s.id));
      const uploadHasDepth = newSticks.length === 0 || sticksHaveDepth(newSticks);
      if (!uploadHasDepth) {
        setDepthConfigOpen(true);
        return;
      }
      setUploadModalOpen(false);
      setShapefileView({ kind: "dropzone" });
    }
  }, [shapefileView, scenarioId, selectedProjectId, uploadShapefile, queryClient]);

  const handleIdPropertyReset = useCallback(() => {
    setShapefileView({ kind: "dropzone" });
    setShapefileError(null);
  }, []);

  const handleZipFileSelected = useCallback(
    async (file: File) => {
      setShapefileError(null);

      const priorIds = new Set(
        (
          queryClient.getQueryData<Stick[]>([
            ScenarioStickListKey,
            { $oid: scenarioId }
          ]) ?? []
        ).map((s) => s.id)
      );

      const success = await handleZipUpload({
        file,
        scenarioId,
        upload: uploadShapefile,
        queryClient,
        toastContainerId: ScenarioToastContainerId,
        onError: setShapefileError,
        onIdPropertyRequired: (f, properties) => {
          setShapefileView({
            kind: "selectIdProperty",
            file: f,
            properties,
            selectedProperty: properties[0] ?? ""
          });
        }
      });
      if (success) {
        const freshSticks =
          queryClient.getQueryData<Stick[]>([
            ScenarioStickListKey,
            { $oid: scenarioId }
          ]) ?? [];
        const scenarioHasDepth = freshSticks.length === 0 || sticksHaveDepth(freshSticks);
        patchScenarioListDepthFlag(
          queryClient,
          selectedProjectId,
          scenarioId,
          scenarioHasDepth
        );
        const newSticks = freshSticks.filter((s) => !priorIds.has(s.id));
        const uploadHasDepth = newSticks.length === 0 || sticksHaveDepth(newSticks);
        if (!uploadHasDepth) {
          setDepthConfigOpen(true);
          return;
        }
        setUploadModalOpen(false);
        setShapefileView({ kind: "dropzone" });
      }
    },
    [scenarioId, selectedProjectId, uploadShapefile, queryClient]
  );

  // Counter-based drag tracking for the ZIP drop zone: prevents flicker when
  // the cursor moves between child elements inside the zone.
  const zipDragCounterRef = useRef(0);

  const handleZipDropZoneDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    zipDragCounterRef.current += 1;
    setZipDragActive(true);
  }, []);

  const handleZipDropZoneDragLeave = useCallback(() => {
    zipDragCounterRef.current -= 1;
    if (zipDragCounterRef.current === 0) {
      setZipDragActive(false);
    }
  }, []);

  const handleZipDropZoneDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleZipDropZoneDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Snapshot files synchronously before any await — the synthetic event
      // (including dataTransfer) is pooled and nullified on the next tick.
      const files = Array.from(e.dataTransfer.files ?? []);

      zipDragCounterRef.current = 0;
      setZipDragActive(false);

      if (files.length === 0) return;

      const zipFile = files.find((f) => f.name.toLowerCase().endsWith(".zip"));
      const nonZipFiles = files.filter((f) => !f.name.toLowerCase().endsWith(".zip"));

      const zipFileErrors = nonZipFiles.map((f) => `"${f.name}" is not a ZIP file.`);
      if (zipFileErrors.length > 0) {
        setShapefileError(zipFileErrors.join(" "));
      }

      if (zipFile) {
        await handleZipFileSelected(zipFile);
      }
    },
    [handleZipFileSelected]
  );

  // ─── Two-file CSV handlers ────────────────────────────────────────────────

  const loadFileIntoSlot = useCallback(async (file: File, target: FileTarget) => {
    const requiredFields =
      target === "surface" ? SURFACE_REQUIRED_FIELDS : STATIONS_REQUIRED_FIELDS;

    try {
      const headers = await extractCsvHeaders(file);
      const mappingRows = autoMapRequiredFields(requiredFields, headers);

      setCsvError(null);
      dispatchCsvModal({
        type: "SET_FILE",
        target,
        payload: { file, headers, mappingRows }
      });
    } catch (error) {
      setCsvError(error instanceof Error ? error.message : "Failed to read CSV headers.");
    }
  }, []);

  /**
   * Reads the first-line headers of a legacy single-file CSV, auto-maps
   * columns via fuzzy matching, and loads the result into the legacy state.
   * Accepts optional pre-read headers to avoid a redundant file read when
   * called from classifyAndLoadCsvFile which has already parsed line 1.
   */
  const handleLegacyFileLoad = useCallback(
    async (file: File, preReadHeaders?: string[]) => {
      try {
        const headers = preReadHeaders ?? (await extractLegacyCsvHeaders(file));
        const autoMapped = autoMapCsvHeaders(headers);

        const mappingRows: LegacyMappingRow[] = SURVEY_REQUIRED_COLUMNS.map((col) => {
          let matchedHeader = "";
          for (const [csvHeader, schemaCol] of autoMapped) {
            if (schemaCol === col.value) {
              matchedHeader = csvHeader;
              break;
            }
          }
          return {
            schemaColumn: col.value,
            label: col.label,
            csvHeader: matchedHeader
          };
        });

        setCsvError(null);
        dispatchLegacyCsv({
          type: "SET_FILE",
          payload: { file, headers, mappingRows }
        });
      } catch (error) {
        setCsvError(
          error instanceof Error ? error.message : "Failed to read CSV headers."
        );
      }
    },
    []
  );

  /**
   * Auto-detects the CSV format (two-file with CRS preamble vs legacy
   * single-file) and routes the file to the correct loading pipeline.
   *
   * Detection strategy:
   *   1. Read line 1 of the file.
   *   2. If it starts with "Coordinate system" → two-file format with
   *      preamble. Skip the preamble, classify as surface / stations.
   *   3. Otherwise check for recognisable survey column headers on line 1
   *      (UWI, MD, Azimuth, Inclination) → legacy single-file format.
   *
   * When switching between formats, the other format's state is cleared so
   * that the derived `detectedCsvFormat` always reflects a single format.
   */
  const classifyAndLoadCsvFile = useCallback(
    async (file: File): Promise<void> => {
      try {
        // Read line 1 to decide which format this file uses.
        const firstLineHeaders = await extractLegacyCsvHeaders(file);
        const hasPreamble =
          firstLineHeaders[0]?.toLowerCase().trim() === "coordinate system";

        if (hasPreamble) {
          // ── Two-file format (CRS preamble present) ────────────────────
          const headers = await extractCsvHeaders(file);
          const target = classifyCsvFile(headers);

          if (target !== null) {
            // Clear legacy state so detectedCsvFormat resolves to "twoFile".
            dispatchLegacyCsv({ type: "RESET" });
            await loadFileIntoSlot(file, target);
            return;
          }

          setCsvError(
            `Could not classify "${file.name}" as a surface locations file (needs KB) or a survey stations file (needs Azimuth / MD).`
          );
          return;
        }

        // ── No preamble — check for legacy survey headers on line 1 ─────
        const normHeaders = firstLineHeaders.map((h) => h.toLowerCase().trim());
        const looksLegacy = normHeaders.some(
          (h) => h === "uwi" || h === "md" || h === "azimuth" || h === "inclination"
        );

        if (looksLegacy) {
          // Clear two-file state so detectedCsvFormat resolves to "legacy".
          dispatchCsvModal({ type: "RESET" });
          await handleLegacyFileLoad(file, firstLineHeaders);
          return;
        }

        setCsvError(
          `Could not determine file type for "${file.name}". ` +
            `Expected a two-file CSV with a CRS preamble, or a legacy single-file CSV ` +
            `with columns such as UWI, MD, Azimuth, or Inclination.`
        );
      } catch {
        setCsvError(`Failed to read CSV headers from "${file.name}".`);
      }
    },
    [loadFileIntoSlot, handleLegacyFileLoad]
  );

  // Counter-based drag tracking: incremented on dragenter, decremented on
  // dragleave. Reaches 0 only when the cursor has truly left the drop zone
  // (not just moved between child elements), preventing flicker.
  const dragCounterRef = useRef(0);

  const handleCombinedDropZoneClick = useCallback(() => {
    if (!csvInputRef) return;
    csvInputRef.value = "";
    csvInputRef.click();
  }, [csvInputRef]);

  const handleCombinedDropZoneDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragCounterRef.current += 1;
      dispatchCsvModal({ type: "SET_COMBINED_DRAG_ACTIVE", payload: true });
    },
    []
  );

  const handleCombinedDropZoneDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Snapshot files synchronously before any await — React pools and
      // nullifies the synthetic event (including dataTransfer) on the next tick.
      const files = Array.from(e.dataTransfer.files ?? []);

      dragCounterRef.current = 0;
      dispatchCsvModal({ type: "SET_COMBINED_DRAG_ACTIVE", payload: false });

      if (files.length === 0) return;

      const csvFiles = files.filter((f) => f.name.toLowerCase().endsWith(".csv"));
      const nonCsvFiles = files.filter((f) => !f.name.toLowerCase().endsWith(".csv"));

      const csvFileErrors = nonCsvFiles.map((f) => `"${f.name}" is not a CSV file.`);
      if (csvFileErrors.length > 0) {
        setCsvError(csvFileErrors.join(" "));
      }

      await Promise.all(csvFiles.map(classifyAndLoadCsvFile));
    },
    [classifyAndLoadCsvFile]
  );

  const handleCombinedDropZoneDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
    },
    []
  );

  const handleCombinedDropZoneDragLeave = useCallback(() => {
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      dispatchCsvModal({ type: "SET_COMBINED_DRAG_ACTIVE", payload: false });
    }
  }, []);

  const handleCsvFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length === 0) return;

      await Promise.all(files.map(classifyAndLoadCsvFile));
    },
    [classifyAndLoadCsvFile]
  );

  const handleMappingRowChange = useCallback(
    (target: FileTarget, index: number, csvColumn: string) => {
      dispatchCsvModal({
        type: "SET_MAPPING_ROW",
        target,
        payload: { index, csvColumn }
      });
    },
    []
  );

  const handleCsvUploadOk = useCallback(async () => {
    if (!isTwoFileCsvFormValid(csvModal) || !scenarioId) return;

    const { surface, stations } = csvModal;
    if (!surface.file || !stations.file) return;

    // Keep the modal open and files in state while the upload is in-flight so
    // the user can retry if it fails. Close and reset only on success.
    setCsvError(null);

    const priorIds = new Set(
      (
        queryClient.getQueryData<Stick[]>([ScenarioStickListKey, { $oid: scenarioId }]) ??
        []
      ).map((s) => s.id)
    );

    const success = await handleTwoFileCsvUpload({
      surfaceFile: surface.file,
      stationsFile: stations.file,
      scenarioId,
      upload: uploadCsv,
      queryClient,
      toastContainerId: ScenarioToastContainerId,
      onError: setCsvError
    });

    if (success) {
      const freshSticks =
        queryClient.getQueryData<Stick[]>([ScenarioStickListKey, { $oid: scenarioId }]) ??
        [];
      const scenarioHasDepth = freshSticks.length === 0 || sticksHaveDepth(freshSticks);
      patchScenarioListDepthFlag(
        queryClient,
        selectedProjectId,
        scenarioId,
        scenarioHasDepth
      );
      const newSticks = freshSticks.filter((s) => !priorIds.has(s.id));
      const uploadHasDepth = newSticks.length === 0 || sticksHaveDepth(newSticks);
      if (!uploadHasDepth) {
        setDepthConfigOpen(true);
        return;
      }
      setUploadModalOpen(false);
      dispatchCsvModal({ type: "RESET" });
    }
  }, [csvModal, scenarioId, selectedProjectId, uploadCsv, queryClient]);

  const isFormValid = isTwoFileCsvFormValid(csvModal);
  const isLegacyFormValidState = isLegacyCsvFormValid(legacyCsv);

  // ─── Legacy single-file CSV handlers ──────────────────────────────────────

  const handleLegacyMappingRowChange = useCallback((index: number, csvHeader: string) => {
    dispatchLegacyCsv({
      type: "SET_MAPPING_ROW",
      payload: { index, csvHeader }
    });
  }, []);

  const handleLegacyUploadOk = useCallback(async () => {
    if (!isLegacyCsvFormValid(legacyCsv) || !scenarioId) return;
    if (!legacyCsv.file) return;

    const columnMapping: Record<string, string> = {};
    for (const row of legacyCsv.mappingRows) {
      if (row.csvHeader) {
        columnMapping[row.csvHeader] = row.schemaColumn;
      }
    }

    setCsvError(null);

    const priorIds = new Set(
      (
        queryClient.getQueryData<Stick[]>([ScenarioStickListKey, { $oid: scenarioId }]) ??
        []
      ).map((s) => s.id)
    );

    const success = await handleLegacyCsvUpload({
      file: legacyCsv.file,
      utmZone: legacyCsv.utmZone,
      datum: legacyCsv.datum,
      columnMapping,
      scenarioId,
      upload: (params) => uploadLegacyCsv(params),
      queryClient,
      toastContainerId: ScenarioToastContainerId,
      onError: setCsvError
    });

    if (success) {
      const freshSticks =
        queryClient.getQueryData<Stick[]>([ScenarioStickListKey, { $oid: scenarioId }]) ??
        [];
      const scenarioHasDepth = freshSticks.length === 0 || sticksHaveDepth(freshSticks);
      patchScenarioListDepthFlag(
        queryClient,
        selectedProjectId,
        scenarioId,
        scenarioHasDepth
      );
      const newSticks = freshSticks.filter((s) => !priorIds.has(s.id));
      const uploadHasDepth = newSticks.length === 0 || sticksHaveDepth(newSticks);
      if (!uploadHasDepth) {
        setDepthConfigOpen(true);
        return;
      }
      setUploadModalOpen(false);
      dispatchLegacyCsv({ type: "RESET" });
      dispatchCsvModal({ type: "RESET" });
    }
  }, [legacyCsv, scenarioId, selectedProjectId, uploadLegacyCsv, queryClient]);

  // ─── Render helpers ───────────────────────────────────────────────────────

  const renderDropZone = () => {
    const { surface, stations, combinedDragActive } = csvModal;
    const hasSurface = !!surface.file;
    const hasStations = !!stations.file;
    const hasLegacy = !!legacyCsv.file;
    const allTwoFileFilled = hasSurface && hasStations;
    const isActive = combinedDragActive;
    const isFilled = allTwoFileFilled || hasLegacy;

    return (
      <CombinedDropZone
        $active={isActive}
        $allFilled={isFilled}
        data-testid="drop-zone-combined"
        onClick={handleCombinedDropZoneClick}
        onDragEnter={handleCombinedDropZoneDragEnter}
        onDrop={handleCombinedDropZoneDrop}
        onDragOver={handleCombinedDropZoneDragOver}
        onDragLeave={handleCombinedDropZoneDragLeave}>
        {hasLegacy ? (
          /* ── Legacy: single file chip ───────────────────────────────── */
          <CombinedDropZoneFiles>
            <CombinedDropZoneFileChip>
              <CombinedDropZoneFileChipLabel>CSV</CombinedDropZoneFileChipLabel>
              {legacyCsv.file?.name}
              <RemoveFileButton
                data-testid="remove-file-legacy"
                title="Remove CSV file"
                style={{ pointerEvents: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatchLegacyCsv({ type: "CLEAR_FILE" });
                }}>
                ×
              </RemoveFileButton>
            </CombinedDropZoneFileChip>
          </CombinedDropZoneFiles>
        ) : allTwoFileFilled ? (
          /* ── Two-file: both filled ──────────────────────────────────── */
          <CombinedDropZoneFiles>
            <CombinedDropZoneFileChip>
              <CombinedDropZoneFileChipLabel>Locations</CombinedDropZoneFileChipLabel>
              {surface.file?.name}
              <RemoveFileButton
                data-testid="remove-file-surface"
                title="Remove surface locations file"
                style={{ pointerEvents: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatchCsvModal({ type: "CLEAR_FILE", target: "surface" });
                }}>
                ×
              </RemoveFileButton>
            </CombinedDropZoneFileChip>
            <CombinedDropZoneFileChip>
              <CombinedDropZoneFileChipLabel>Stations</CombinedDropZoneFileChipLabel>
              {stations.file?.name}
              <RemoveFileButton
                data-testid="remove-file-stations"
                title="Remove survey stations file"
                style={{ pointerEvents: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatchCsvModal({ type: "CLEAR_FILE", target: "stations" });
                }}>
                ×
              </RemoveFileButton>
            </CombinedDropZoneFileChip>
          </CombinedDropZoneFiles>
        ) : hasSurface || hasStations ? (
          /* ── Two-file: one slot filled ──────────────────────────────── */
          <CombinedDropZoneFiles>
            {hasSurface && (
              <CombinedDropZoneFileChip>
                <CombinedDropZoneFileChipLabel>Locations</CombinedDropZoneFileChipLabel>
                {surface.file?.name}
                <RemoveFileButton
                  data-testid="remove-file-surface"
                  title="Remove surface locations file"
                  style={{ pointerEvents: "auto" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatchCsvModal({ type: "CLEAR_FILE", target: "surface" });
                  }}>
                  ×
                </RemoveFileButton>
              </CombinedDropZoneFileChip>
            )}
            {hasStations && (
              <CombinedDropZoneFileChip>
                <CombinedDropZoneFileChipLabel>Stations</CombinedDropZoneFileChipLabel>
                {stations.file?.name}
                <RemoveFileButton
                  data-testid="remove-file-stations"
                  title="Remove survey stations file"
                  style={{ pointerEvents: "auto" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatchCsvModal({ type: "CLEAR_FILE", target: "stations" });
                  }}>
                  ×
                </RemoveFileButton>
              </CombinedDropZoneFileChip>
            )}
            <CombinedDropZoneText>
              Drop the remaining file here, or{" "}
              <span style={{ color: "#2dd4bf" }}>browse</span>
            </CombinedDropZoneText>
          </CombinedDropZoneFiles>
        ) : (
          /* ── Neutral: nothing loaded yet ────────────────────────────── */
          <CombinedDropZoneText>
            Drag &amp; drop your CSV file(s) here, or{" "}
            <span style={{ color: "#2dd4bf" }}>browse</span>
            <br />
            <span style={{ fontSize: "11.5px", color: "#9ca3af" }}>
              Format is detected automatically from file contents
            </span>
          </CombinedDropZoneText>
        )}
      </CombinedDropZone>
    );
  };

  const renderMappingTable = (target: FileTarget) => {
    const slot = csvModal[target];

    if (!slot.file) return null;

    const columnOptions = slot.headers.map((h) => ({ label: h, value: h }));

    return (
      <MappingSection>
        <MappingHint>
          Map each required field to the corresponding column in your CSV.
        </MappingHint>
        <MappingTableContainer>
          <MappingTableHeaderRow>
            <MappingTableHeaderCell>Required Field</MappingTableHeaderCell>
            <MappingTableHeaderCell>Your CSV Column</MappingTableHeaderCell>
          </MappingTableHeaderRow>

          {slot.mappingRows.map((row, index) => {
            const isMapped = row.csvColumn !== "";
            return (
              <MappingRow key={row.requiredField}>
                <RequiredFieldLabel>
                  {row.requiredField}
                  <RequiredBadge>*</RequiredBadge>
                </RequiredFieldLabel>
                <Select<string>
                  data-testid={`mapping-select-${target}-${index}`}
                  style={{ width: "100%" }}
                  className={isMapped ? "csv-select-mapped" : undefined}
                  placeholder="— select column —"
                  value={row.csvColumn || undefined}
                  onChange={(v) => handleMappingRowChange(target, index, v ?? "")}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={columnOptions.map((opt) => ({
                    label: opt.label,
                    value: opt.value,
                    disabled: slot.mappingRows.some(
                      (r, i) => i !== index && r.csvColumn === opt.value
                    )
                  }))}
                />
              </MappingRow>
            );
          })}
        </MappingTableContainer>
      </MappingSection>
    );
  };

  const renderLegacyFormFields = () => (
    <>
      <LegacyFormRow>
        <FormField>
          <FormFieldLabel>UTM Zone *</FormFieldLabel>
          <Input
            data-testid="legacy-utm-zone"
            placeholder="e.g. 11N"
            value={legacyCsv.utmZone}
            onChange={(e) =>
              dispatchLegacyCsv({
                type: "SET_UTM_ZONE",
                payload: e.target.value.trim()
              })
            }
            style={{ width: "100%" }}
          />
        </FormField>
        <FormField>
          <FormFieldLabel>Datum *</FormFieldLabel>
          <Select<string>
            data-testid="legacy-datum"
            placeholder="Select datum"
            value={legacyCsv.datum || undefined}
            onChange={(v) =>
              dispatchLegacyCsv({
                type: "SET_DATUM",
                payload: (v ?? "") as SupportedDatum | ""
              })
            }
            allowClear
            style={{ width: "100%" }}
            options={[
              { label: "NAD83", value: "NAD83" },
              { label: "NAD27", value: "NAD27" },
              { label: "WGS84", value: "WGS84" }
            ]}
          />
        </FormField>
      </LegacyFormRow>

      {legacyCsv.file && (
        <LegacyMappingWrapper>
          <MappingHint>
            Map each column to the corresponding field in your CSV. Columns marked with *
            are required.
          </MappingHint>
          <DepthHint>
            At least one depth path is required: <b>Subsea</b>, or both <b>TVD</b> and{" "}
            <b>KB</b>.
          </DepthHint>
          <MappingTableContainer>
            <MappingTableHeaderRow>
              <MappingTableHeaderCell>Field</MappingTableHeaderCell>
              <MappingTableHeaderCell>Your CSV Column</MappingTableHeaderCell>
            </MappingTableHeaderRow>
            {legacyCsv.mappingRows.map((row, index) => {
              const isMapped = row.csvHeader !== "";
              const isRequired = ALWAYS_REQUIRED_COLUMNS.includes(row.schemaColumn);
              return (
                <MappingRow key={row.schemaColumn}>
                  <RequiredFieldLabel>
                    {row.label}
                    {isRequired && <RequiredBadge>*</RequiredBadge>}
                  </RequiredFieldLabel>
                  <Select<string>
                    data-testid={`mapping-select-legacy-${index}`}
                    style={{ width: "100%" }}
                    className={isMapped ? "csv-select-mapped" : undefined}
                    placeholder="— select column —"
                    value={row.csvHeader || undefined}
                    onChange={(v) => handleLegacyMappingRowChange(index, v ?? "")}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      String(option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={legacyCsv.headers.map((h) => ({
                      label: h,
                      value: h,
                      disabled: legacyCsv.mappingRows.some(
                        (r, i) => i !== index && r.csvHeader === h
                      )
                    }))}
                  />
                </MappingRow>
              );
            })}
          </MappingTableContainer>
        </LegacyMappingWrapper>
      )}
    </>
  );

  const renderCsvTabContent = () => (
    <CsvTabBody>
      {csvError && (
        <InlineError style={{ marginBottom: 12 }}>
          <CloseCircleOutlined style={{ marginTop: 2, flexShrink: 0 }} />
          {csvError}
        </InlineError>
      )}

      {/* ── Unified drop zone (auto-detects format) ──────────────────── */}
      {renderDropZone()}

      {/* ── Two-file: per-file mapping columns ───────────────────────── */}
      {detectedCsvFormat === "twoFile" && (
        <CsvColumnsGrid>
          <FileSection>
            <FileSectionTitle>Surface Locations File</FileSectionTitle>
            {renderMappingTable("surface")}
          </FileSection>
          <FileSection>
            <FileSectionTitle>Survey Stations File</FileSectionTitle>
            {renderMappingTable("stations")}
          </FileSection>
        </CsvColumnsGrid>
      )}

      {/* ── Legacy: UTM zone, datum, column mapping ──────────────────── */}
      {detectedCsvFormat === "legacy" && renderLegacyFormFields()}
    </CsvTabBody>
  );

  const zoneOptions = useMemo(() => {
    if (depthModelSource === "") {
      return ipdbZones.map((n) => ({ label: n, value: n }));
    }
    if (!geoMapLayers) return [];
    const source = privateSources?.find((s) => s.source === depthModelSource);
    if (!source) return [];
    const plays = new Set(source.plays);
    return [...new Set(geoMapLayers.filter((l) => plays.has(l.group)).map((l) => l.name))]
      .sort()
      .map((n) => ({ label: n, value: n }));
  }, [ipdbZones, geoMapLayers, privateSources, depthModelSource]);

  const sourceOptions = useMemo(
    () => [
      { label: EVA_RESEARCH_NAME, value: "" },
      ...(privateSources ?? []).map((s) => ({ label: s.source, value: s.source }))
    ],
    [privateSources]
  );

  const handleApplyDepths = useCallback(async () => {
    if (!scenarioId) return;
    if (!depthZone) {
      setDepthError("Reference zone is required.");
      return;
    }
    if (!depthOffset || !VERTICAL_OFFSET_PATTERN.test(depthOffset)) {
      setDepthError("Use format T-40, T+10, B-20, B+0");
      return;
    }
    setDepthError(null);
    try {
      await applyDepths({
        scenarioId,
        referenceZone: depthZone,
        verticalOffset: depthOffset,
        zoneSource: depthModelSource === "" ? "EVA_RESEARCH" : "ORGANIZATION"
      });
      setUploadModalOpen(false);
      setDepthConfigOpen(false);
      setDepthModelSource("");
      setDepthZone("");
      setDepthOffset("");
      dispatchCsvModal({ type: "RESET" });
      dispatchLegacyCsv({ type: "RESET" });
      setShapefileView({ kind: "dropzone" });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Failed to apply depths.";
      setDepthError(msg);
    }
  }, [scenarioId, depthZone, depthOffset, depthModelSource, applyDepths]);

  const renderDepthConfigForm = () => (
    <ShapefileTabBody>
      <Alert
        message={DEPTH_MISSING_WARNING}
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
      {depthError && (
        <InlineError style={{ marginBottom: 12 }}>
          <CloseCircleOutlined style={{ marginTop: 2, flexShrink: 0 }} />
          {depthError}
        </InlineError>
      )}
      <Form layout="vertical" requiredMark={false}>
        <Form.Item label="Model Source">
          <Select
            options={sourceOptions}
            value={depthModelSource}
            onChange={(v) => {
              setDepthModelSource(v);
              setDepthZone("");
            }}
          />
        </Form.Item>
        <Form.Item label="Reference Zone" required>
          <Select
            showSearch
            placeholder="Select zone"
            options={zoneOptions}
            value={depthZone || undefined}
            onChange={(v) => setDepthZone(v ?? "")}
            filterOption={(input, option) =>
              String(option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item label="Vertical Offset" required>
          <Input
            placeholder="e.g. T-40"
            value={depthOffset}
            onChange={(e) => setDepthOffset(e.target.value.trim())}
          />
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
            Format: T-40, T+10, B-20, B+0
          </div>
        </Form.Item>
      </Form>
    </ShapefileTabBody>
  );

  const renderShapefileTabContent = () => {
    if (shapefileView.kind === "selectIdProperty") {
      const { file, properties, selectedProperty } = shapefileView;
      return (
        <>
          {shapefileError && (
            <InlineError style={{ marginBottom: 16 }}>
              <CloseCircleOutlined style={{ marginTop: 2, flexShrink: 0 }} />
              {shapefileError}
            </InlineError>
          )}
          <div style={{ marginBottom: 16 }}>
            <CombinedDropZoneFileChip style={{ pointerEvents: "auto" }}>
              <CombinedDropZoneFileChipLabel>ZIP</CombinedDropZoneFileChipLabel>
              {file.name}
            </CombinedDropZoneFileChip>
          </div>
          <IdPropertyDescription>
            This shapefile contains multiple text properties. Choose the one that uniquely
            identifies each well.
          </IdPropertyDescription>
          <Select
            style={{ width: "100%", marginBottom: 20 }}
            placeholder="Select ID property"
            value={selectedProperty || undefined}
            onChange={handleIdPropertySelect}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={properties.map((p) => ({ label: p, value: p }))}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button onClick={handleIdPropertyReset}>Back</Button>
            <Button
              type="primary"
              disabled={!selectedProperty}
              loading={isShapefileLoading}
              onClick={handleIdPropertyConfirm}>
              Upload
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        {shapefileError && (
          <InlineError style={{ marginBottom: 12 }}>
            <CloseCircleOutlined style={{ marginTop: 2, flexShrink: 0 }} />
            {shapefileError}
          </InlineError>
        )}
        <CombinedDropZone
          $active={zipDragActive}
          $allFilled={false}
          data-testid="drop-zone-zip"
          onClick={handleZipDropZoneClick}
          onDragEnter={handleZipDropZoneDragEnter}
          onDrop={handleZipDropZoneDrop}
          onDragOver={handleZipDropZoneDragOver}
          onDragLeave={handleZipDropZoneDragLeave}>
          <CombinedDropZoneText>
            Drag &amp; drop your ZIP file here, or{" "}
            <span style={{ color: "#2dd4bf" }}>browse</span>
            <br />
            <span style={{ fontSize: "11.5px", color: "#9ca3af" }}>
              Package <TypeCardGuidanceHighlight>.shp</TypeCardGuidanceHighlight>
              {", "}
              <TypeCardGuidanceHighlight>.prj</TypeCardGuidanceHighlight>
              {", "}
              <TypeCardGuidanceHighlight>.shx</TypeCardGuidanceHighlight>
              {" and "}
              <TypeCardGuidanceHighlight>.dbf</TypeCardGuidanceHighlight>
              {" into a flat ZIP — files must sit at the root, not inside a folder"}
            </span>
          </CombinedDropZoneText>
        </CombinedDropZone>
      </>
    );
  };

  return (
    <RootContainer>
      <Button icon={<TableOutlined />} onClick={modal.show} disabled={!scenarioId}>
        Manage Stick Data
      </Button>

      {/* Hidden file input for ZIP uploads */}
      <input
        ref={setZipInputRef}
        type="file"
        accept=".zip"
        style={{ display: "none" }}
        data-testid="zip-file-input"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) {
            await handleZipFileSelected(file);
          }
        }}
      />

      {/* Hidden file input for CSV uploads — multiple allows selecting both files at once */}
      <input
        ref={setCsvInputRef}
        type="file"
        accept=".csv"
        multiple
        style={{ display: "none" }}
        data-testid="csv-file-input"
        onChange={handleCsvFileInputChange}
      />

      <Button
        role="button"
        data-testid="upload-survey-button"
        icon={<UploadOutlined />}
        loading={isLoading}
        disabled={!scenarioId || isLoading}
        onClick={handleUploadSurveysClick}>
        Upload Surveys
      </Button>

      {/* ── Unified upload modal (tabbed) ────────────────────────────────── */}
      <Modal
        title="Upload Surveys"
        open={uploadModalOpen}
        onCancel={handleUploadModalCancel}
        width={960}
        maskClosable={false}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={handleUploadModalCancel}>
              {depthConfigOpen ? "Skip" : "Cancel"}
            </Button>
            {depthConfigOpen ? (
              <Button
                type="primary"
                loading={isApplyingDepths}
                disabled={!depthZone || !depthOffset}
                onClick={handleApplyDepths}>
                Apply Depth
              </Button>
            ) : (
              activeUploadTab === "csv" &&
              detectedCsvFormat !== null && (
                <Button
                  type="primary"
                  disabled={
                    detectedCsvFormat === "twoFile"
                      ? !isFormValid
                      : !isLegacyFormValidState
                  }
                  loading={isCsvLoading || isLegacyCsvLoading}
                  onClick={
                    detectedCsvFormat === "twoFile"
                      ? handleCsvUploadOk
                      : handleLegacyUploadOk
                  }>
                  Upload
                </Button>
              )
            )}
          </div>
        }>
        <ModalContentWrapper>
          {isLoading && (
            <ModalLoadingOverlay>
              <IconSpinner size={32} />
              <ModalLoadingTip>
                {isShapefileLoading ? "Uploading shapefile…" : "Uploading surveys…"}
              </ModalLoadingTip>
            </ModalLoadingOverlay>
          )}
          {depthConfigOpen ? (
            renderDepthConfigForm()
          ) : (
            <UploadTabsWrapper>
              <Tabs
                activeKey={activeUploadTab}
                onChange={(key) => {
                  if (!isLoading) setActiveUploadTab(key as "shapefile" | "csv");
                }}
                items={[
                  {
                    key: "csv",
                    label: "CSV (Directional)",
                    children: renderCsvTabContent()
                  },
                  {
                    key: "shapefile",
                    label: "Shapefile (.zip)",
                    children: (
                      <ShapefileTabBody>{renderShapefileTabContent()}</ShapefileTabBody>
                    )
                  }
                ]}
              />
            </UploadTabsWrapper>
          )}
        </ModalContentWrapper>
      </Modal>

      {/* ── Stick Data Management modal ──────────────────────────────────── */}
      <StyledModal
        title="Stick Data Management"
        open={modal.open}
        onCancel={modal.hide}
        footer={null}
        width="90%"
        centered
        maskClosable={false}>
        <StickDataManagement
          scenarioId={scenarioId}
          stickData={stickData}
          schema={scenarioName}
          wells={sticks.map((stick) => ({ uwid: stick.id }))}
          fields={state.scenarioFields.map((f) => ({ ...f }))}
          onFieldsChange={handleFieldsChange}
        />
      </StyledModal>
    </RootContainer>
  );
};

export default HeaderButtons;
